import { Building2, ArrowRight, BookOpen, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DdqSessionQuestion } from '@/types/api.types';
import { buildProvenanceTrace } from '../utils/provenance';

export interface ProvenanceTraceProps {
  question: DdqSessionQuestion;
  className?: string;
}

/**
 * Renders the provenance of an answer — the product's differentiator made
 * visible. A vault-grounded answer shows its full chain back to the warehouse;
 * knowledge-base and AI answers show an honest one-line note; firm-required and
 * unknown sources render nothing.
 */
export function ProvenanceTrace({ question, className }: ProvenanceTraceProps) {
  const trace = buildProvenanceTrace(question);

  if (trace.kind === 'none') {
    return null;
  }

  return (
    <div
      className={cn(
        'rounded-lg border border-border px-3 py-2.5',
        className,
      )}
    >
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
        Provenance
      </p>

      {trace.kind === 'vault' && (
        <div className="flex flex-wrap items-center gap-1.5 font-mono text-xs">
          {trace.steps.map((step, i) => (
            <span key={i} className="flex items-center gap-1.5">
              {i === 0 && (
                <Building2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
              )}
              <span
                className={cn(
                  i === 0 && 'text-emerald-700 dark:text-emerald-300',
                  i === trace.steps.length - 1 && i !== 0 && 'text-foreground',
                  i !== 0 && i !== trace.steps.length - 1 && 'text-muted-foreground',
                )}
              >
                {step}
              </span>
              {i < trace.steps.length - 1 && (
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />
              )}
            </span>
          ))}
        </div>
      )}

      {trace.kind === 'knowledge_base' && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{trace.note}</span>
        </div>
      )}

      {trace.kind === 'llm' && (
        <div className="flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400">
          <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{trace.note}</span>
        </div>
      )}
    </div>
  );
}
